import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, Empty, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faEnvelopeOpen,
  faEnvelope,
  faCircleCheck,
  faTriangleExclamation,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { setCurrentMail } from "../../../Slices/notification";
import OutlookFolders from "./OutlookFolders";

const SalesOutlookMailbox = () => {
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const sharedemails = useSelector((state) => state?.sharedEmail?.sharedemails);
  const emailByUser = useSelector((state) => state.EmailData.byUser);

  // Build the sales email list for this user
  const emailMap = useMemo(
    () =>
      (sharedemails || []).reduce((acc, item) => {
        acc[item._id] = item.email;
        return acc;
      }, {}),
    [sharedemails]
  );

  const salesEmails = useMemo(() => {
    return (
      loginUser?.salesEmailSettings
        ?.filter((item) => item.status === true || item.status === "true")
        ?.map((item) => item.email)
        ?.filter(Boolean) || []
    );
  }, [loginUser]);

  const [activeEmail, setActiveEmail] = useState(null);

  useEffect(() => {
    if (salesEmails.length > 0 && !activeEmail) {
      const first = salesEmails[0];
      setActiveEmail(first);
      dispatch(setCurrentMail(first));
    }
  }, [salesEmails, activeEmail, dispatch]);

  const handleEmailSwitch = (value) => {
    setActiveEmail(value);
    dispatch(setCurrentMail(value));
  };

  // Compute analytics — aggregate if '__all__' selected
  const stats = useMemo(() => {
    const accounts = activeEmail === '__all__' ? salesEmails : (activeEmail ? [activeEmail] : []);
    let sentCount = 0, totalInbox = 0, readCount = 0;
    accounts.forEach(acc => {
      const userData = emailByUser[acc];
      sentCount  += userData?.folders?.sentitems?.data?.length || 0;
      const inbox = userData?.folders?.inbox?.data || [];
      totalInbox += inbox.length;
      readCount  += inbox.filter(m => m.isRead).length;
    });
    return { sent: sentCount, inboxTotal: totalInbox, read: readCount, unread: totalInbox - readCount };
  }, [activeEmail, emailByUser, salesEmails]);

  if (salesEmails.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
          gap: 16,
        }}
      >
        <Empty
          description={
            <span style={{ color: "#6b7280", fontSize: 14 }}>
              No sales email accounts assigned to you.
              <br />
              Ask your admin to assign sales emails from{" "}
              <strong>Access Management → Outlook → Sales Outlook</strong>.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", background: "#f8fafc" }}>
      {/* ── Header bar with inline stats ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          padding: "10px 24px",
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        {/* Title */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #3b82f6, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FontAwesomeIcon icon={faChartLine} style={{ color: "#fff", fontSize: 14 }} />
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Sales Outreach Hub</div>
            <div style={{ color: "#94a3b8", fontSize: 11 }}>{salesEmails.length} account{salesEmails.length !== 1 ? "s" : ""} assigned</div>
          </div>
        </div>

        {/* Account switcher */}
        {salesEmails.length > 0 && (
          <Select
            value={activeEmail}
            onChange={handleEmailSwitch}
            style={{ minWidth: 220 }}
            suffixIcon={<FontAwesomeIcon icon={faChevronDown} style={{ color: "#94a3b8", fontSize: 11 }} />}
            options={[
              { value: '__all__', label: '📬 All Emails' },
              ...salesEmails.map((em) => ({ value: em, label: em })),
            ]}
            styles={{ popup: { root: { zIndex: 9999 } } }}
          />
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Inline stat pills */}
        {[
          { icon: faEnvelope,           label: "Sent",   value: stats.sent,           color: "#60a5fa" },
          { icon: faCircleCheck,        label: "Inbox",  value: stats.inboxTotal || 0, color: "#34d399" },
          { icon: faEnvelopeOpen,       label: "Read",   value: stats.read || 0,       color: "#c084fc" },
          { icon: faTriangleExclamation,label: "Unread", value: stats.unread || 0,     color: "#fbbf24" },
        ].map(({ icon, label, value, color }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "5px 12px" }}>
            <FontAwesomeIcon icon={icon} style={{ color, fontSize: 12 }} />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: 14 }}>{value}</span>
            <span style={{ color: "#94a3b8", fontSize: 11 }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Email Client ── */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        {activeEmail ? (
          <OutlookFolders
            EmailsLists={salesEmails}
            mode={true}
            embedded={true}
          />
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            <Spin size="large" />
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesOutlookMailbox;
